import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Badge, Modal } from "react-bootstrap";
import { FaBell, FaTimes, FaTrash, FaClock, FaRedo, FaTag } from "react-icons/fa";
import io from "socket.io-client";
import axios from "axios";

const NotificationBell = ({ userData }) => {
  const [notifications, setNotifications] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [shakeBell, setShakeBell] = useState(false);
  const [socket, setSocket] = useState(null);

  // Modal State for Inbox-Reminder Pop-up Window
  const [selectedReminder, setSelectedReminder] = useState(null);

  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const audioRef = useRef(null);

  // Helper to extract timestamp string regardless of format ($date or ISO string)
  const parseTimestamp = (ts) => {
    if (!ts) return "-";
    if (typeof ts === "object" && ts.$date) return new Date(ts.$date).toLocaleString();
    return new Date(ts).toLocaleString();
  };

  //==========================
  // 1. INITIAL FETCH (OLD NOTIFICATIONS)
  //==========================
  const fetchOld = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_COLLEGE_ERP_API}notification`,
        {
          params: { branch: userData.branch, userId: userData.userId },
          headers: { Authorization: `Bearer ${userData.token}` },
        }
      );
      setNotifications(res.data || []);
    } catch (err) {
      console.error("Fetch old notifications failed:", err);
    }
  };

  useEffect(() => {
    if (!userData?.branch) return;
    fetchOld();
  }, [userData]);

  //==========================
  // 2. SOCKET CONNECTION
  //==========================
  useEffect(() => {
    if (!userData?.branch) return;

    const newSocket = io(process.env.REACT_APP_COLLEGE_ERP_API, {
      transports: ["websocket"],
      query: { branch: userData.branch },
    });

    newSocket.emit("joinBranch", {
      branch: userData.branch,
      role: userData.rights,
      senderId: userData.userId,
    });

    setSocket(newSocket);
    return () => newSocket.disconnect();
  }, [userData]);

  //==========================
  // 3. LISTEN FOR LIVE ALERTS & REMINDERS
  //==========================
  useEffect(() => {
    if (!socket) return;

    const handleIncomingNotification = (msg) => {
      const safeMsg = typeof msg === "object" && msg !== null ? msg : { message: msg };

      const newNote = {
        ...safeMsg,
        _id: safeMsg._id || `temp-${Date.now()}`,
        isRead: false,
      };

      setNotifications((prev) => [newNote, ...prev]);

      setShakeBell(true);
      setTimeout(() => setShakeBell(false), 600);
      setShowPanel(true);

      try {
        audioRef.current?.play();
      } catch (err) {
        console.warn("Audio play failed:", err);
      }
    };

    // Listen to both fee alerts and inbox reminders
    socket.on("purchaseAlert", handleIncomingNotification);
    socket.on("Reminder", handleIncomingNotification);

    return () => {
      socket.off("purchaseAlert", handleIncomingNotification);
      socket.off("Reminder", handleIncomingNotification);
    };
  }, [socket]);

  //==========================
  // 4. DELETE A SINGLE NOTIFICATION
  //==========================
  const deleteOne = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_COLLEGE_ERP_API}notification/${id}`,
        {
          params: { branch: userData.branch, userId: userData.userId },
          headers: { Authorization: `Bearer ${userData.token}` },
        }
      );

      setNotifications((prev) => prev.filter((n) => n._id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleSwipeDelete = (note) => {
    const id = note._id;
    setNotifications((prev) => prev.filter((n) => (n._id || n.tempId) !== id));
    if (id && !id.startsWith("temp-")) {
      deleteOne(id);
    }
  };

  //==========================
  // 5. DELETE ALL
  //==========================
  const clearAll = async () => {
    const deletedNotes = notifications;
    setNotifications([]);
    await new Promise((res) => setTimeout(res, 300));

    try {
      await Promise.all(
        deletedNotes.map((n) =>
          n._id && !n._id.startsWith("temp-")
            ? axios.delete(
                `${process.env.REACT_APP_COLLEGE_ERP_API}notification/${n._id}`,
                {
                  params: { branch: userData.branch, userId: userData.userId },
                  headers: { Authorization: `Bearer ${userData.token}` },
                }
              )
            : Promise.resolve()
        )
      );
    } catch (err) {
      console.error("Clear all failed:", err);
    }
  };

  //==========================
  // 6. CARD CLICK HANDLER
  //==========================
  const handleNotificationClick = (note) => {
    const isReminder = note.type === "Inbox-Reminder" || note.payload?.type === "Inbox-Reminder";

    if (isReminder) {
      setSelectedReminder(note); // Triggers pop-up modal
    } else {
      window.location.href = "/notifications";
    }
  };

  //==========================
  // 7. OUTSIDE CLICK CLOSE
  //==========================
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !bellRef.current.contains(e.target)
      ) {
        setShowPanel(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (
    !["Admin", "Accounts", "Chairman", "SuperAdmin"].includes(userData?.rights)
  )
    return null;

  const unreadCount = notifications.filter((n) => !n.isRead && !n.read).length;

  return (
    <div style={{ position: "fixed", top: "1rem", right: "1.2rem", zIndex: 4000 }}>
      {/* BELL BUTTON */}
      <Button
        ref={bellRef}
        onClick={() => setShowPanel((prev) => !prev)}
        style={{
          position: "relative",
          padding: 0,
          border: "none",
          background: "transparent",
          fontSize: "1.8rem",
          transform: shakeBell ? "rotate(-15deg)" : "rotate(0deg)",
          transition: "0.15s",
        }}
      >
        <FaBell color="#f0ad4e" />
        {unreadCount > 0 && (
          <Badge
            pill
            bg="danger"
            style={{
              position: "absolute",
              top: "-4px",
              right: "-4px",
              fontSize: "0.65rem",
            }}
          >
            {unreadCount}
          </Badge>
        )}
      </Button>

      {/* NOTIFICATION PANEL */}
      <AnimatePresence>
        {showPanel && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            style={{
              position: "absolute",
              top: "2.6rem",
              right: 0,
              width: "330px",
              background: "white",
              borderRadius: "0.6rem",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.15)",
              padding: "0.6rem",
              maxHeight: "380px",
              overflowY: "auto",
            }}
          >
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong style={{ fontSize: "1rem" }}>Notifications</strong>
              {notifications.length > 0 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={clearAll}
                  style={{ padding: "2px 6px", fontSize: "0.75rem" }}
                >
                  <FaTrash size={12} /> Clear
                </Button>
              )}
            </div>

            {/* BODY */}
            {notifications.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                style={{ textAlign: "center", padding: "1rem", color: "#777" }}
              >
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{ repeat: Infinity, duration: 1.3 }}
                  style={{ fontSize: "3.5rem" }}
                >
                  🐣
                </motion.div>
                <div style={{ marginTop: "0.6rem", fontSize: "0.9rem" }}>
                  No Notifications
                </div>
              </motion.div>
            ) : (
              notifications.map((note) => {
                const isReminder =
                  note.type === "Inbox-Reminder" || note.payload?.type === "Inbox-Reminder";
                const payload = note.payload || {};

                return (
                  <motion.div
                    key={note._id || note.tempId || Math.random()}
                    layout
                    initial={{ x: 40, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 120, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 24 }}
                    style={{
                      background: note.isRead || note.read ? "#fafafa" : "#e9f4ff",
                      borderRadius: "0.4rem",
                      padding: "0.55rem",
                      marginBottom: "0.4rem",
                      cursor: "pointer",
                      position: "relative",
                      borderLeft: isReminder ? "4px solid #17a2b8" : "4px solid #007bff",
                    }}
                    onClick={() => handleNotificationClick(note)}
                  >
                    {/* DELETE BUTTON */}
                    <FaTimes
                      size={14}
                      color="#888"
                      style={{
                        position: "absolute",
                        top: "6px",
                        right: "6px",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSwipeDelete(note);
                      }}
                    />

                    {/* RENDERING BASED ON TYPE */}
                    {isReminder ? (
                      <>
                        <div style={{ fontSize: "0.85rem", fontWeight: "700", color: "#17a2b8" }}>
                          🔔 {note.title || payload.subject || "Inbox Reminder"}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#333", marginTop: "2px" }}>
                          {note.message || payload.body || "-"}
                        </div>
                        <div style={{ fontSize: "0.7rem", color: "#666", marginTop: "4px" }}>
                          <span className="me-2">📅 {payload.reminderDate || "-"}</span>
                          {payload.repeatFrequency && (
                            <Badge bg="warning" text="dark" style={{ fontSize: "0.65rem" }}>
                              🔄 {payload.repeatFrequency}
                            </Badge>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: "0.9rem", fontWeight: "600" }}>
                          Invoice: {note?.invoice_id || payload?.invoice_id || "N/A"}
                        </div>
                        <div style={{ fontSize: "0.85rem" }}>
                          {note?.type || payload?.type || "Unknown"}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#555" }}>
                          <strong>Total:</strong> {note?.total_amount || payload?.total_amount || "-"} |
                          <strong> Paid:</strong> {note?.paid_amount || payload?.paid_amount || "-"}
                        </div>
                      </>
                    )}

                    <div style={{ fontSize: "0.7rem", color: "#888", marginTop: "4px" }}>
                      {parseTimestamp(note.Timestamp || payload.Timestamp)}
                    </div>
                  </motion.div>
                );
              })
            )}

            {notifications.length > 0 && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => (window.location.href = "/notifications")}
                style={{ width: "100%", marginTop: "6px" }}
              >
                View All
              </Button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ⚡ POPUP MODAL WINDOW FOR INBOX REMINDERS */}
      <AnimatePresence>
        {selectedReminder && (
          <Modal
            show={!!selectedReminder}
            onHide={() => setSelectedReminder(null)}
            centered
            backdrop="static"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <Modal.Header closeButton style={{ background: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
                <Modal.Title style={{ fontSize: "1.1rem", color: "#17a2b8" }} className="d-flex align-items-center gap-2">
                  <FaClock /> Scheduled Alert Reminder
                </Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ padding: "1.2rem" }}>
                <h5 className="mb-2" style={{ fontWeight: "700", color: "#333" }}>
                  {selectedReminder.title || selectedReminder.payload?.subject || "Reminder"}
                </h5>

                <p className="text-muted" style={{ fontSize: "0.95rem", whiteSpace: "pre-wrap" }}>
                  {selectedReminder.message || selectedReminder.payload?.body || "No message body available."}
                </p>

                <hr />

                <div className="d-flex flex-column gap-2" style={{ fontSize: "0.85rem" }}>
                  <div className="d-flex align-items-center gap-2">
                    <FaTag className="text-secondary" />
                    <strong>Category:</strong>
                    <Badge bg="info">{selectedReminder.payload?.category || "Alert"}</Badge>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <FaClock className="text-secondary" />
                    <strong>Reminder Date:</strong>
                    <span>{selectedReminder.payload?.reminderDate || "-"}</span>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <FaRedo className="text-secondary" />
                    <strong>Frequency:</strong>
                    <Badge bg="warning" text="dark" className="text-capitalize">
                      {selectedReminder.payload?.repeatFrequency || "None"}
                    </Badge>
                  </div>

                  {selectedReminder.payload?.to && (
                    <div className="mt-2">
                      <strong>Recipients: </strong>
                      {Object.entries(selectedReminder.payload.to)
                        .filter(([_, active]) => active)
                        .map(([role]) => (
                          <Badge key={role} bg="secondary" className="me-1 text-capitalize">
                            {role}
                          </Badge>
                        ))}
                    </div>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setSelectedReminder(null)}>
                  Close
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setSelectedReminder(null);
                    window.location.href = "/eventscheduler";
                  }}
                >
                  Go to Inbox
                </Button>
              </Modal.Footer>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;